<template>
  <div class="csv-downloader">
    <v-dialog v-model="dialog" width="500">
      <template v-slot:activator="{ on }">
        <v-btn-group>
          <v-btn ref="quickDownload" color="grey lighten-2" @click="download()" :disabled="preparing || disabled" depressed>
            <v-progress-circular v-if="preparing" indeterminate color="primary" class="mr-2"/>
            <v-icon v-if="!preparing" left>cloud_download</v-icon>
            <span v-if="buttonText">{{ buttonText }}</span>
            <span v-else>Download CSV</span>
          </v-btn>
          <v-btn v-if="customizable" v-on="on" color="grey lighten-3" depressed style="min-width: 0" :disabled="preparing || disabled">
            <v-icon>expand_more</v-icon>
          </v-btn>
        </v-btn-group>
      </template>

      <v-card v-if="customizable">
        <v-card-title class="headline grey lighten-2" primary-title>
          {{ dialogTitle }}
        </v-card-title>

        <v-card-text>
          <p class="font-weight-bold">Please, select the required columns</p>

          <v-checkbox class="mt-0 font-weight-bold"
                      label="All"
                      :input-value="allSelected"
                      :indeterminate="indeterminate"
                      @change="selectAll()"
                      hide-details
                      value
                      :disabled="preparing"/>

          <v-checkbox v-for="(header, headerIndex) in headers" :key="`headers-${headerIndex}`"
                      v-model="selectedHeaders"
                      class="mt-0"
                      :label="header.label"
                      :value="header.value"
                      hide-details
                      :disabled="preparing"/>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn ref="customDownload" color="success darken-2" @click="download(selectedHeaders)" :disabled="preparing || disabled">
            <template v-if="preparing">
              <v-progress-circular color="white" indeterminate></v-progress-circular>
            </template>
            <template v-else>
              Download
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import CsvFile from '@/app/models/CsvFile.model'
import { Bus } from '@/bus'

export default {
  name: 'CsvDownloader',
  props: {
    /**
     * Filename
     */
    name: {
      type: String,
      required: true
    },
    /**
     * Download button text
     */
    buttonText: {
      type: String,
      required: false
    },
    fields: {
      type: Object,
      required: true
    },
    /**
     * csv file data
     */
    datasource: {
      type: Function,
      required: true
    },
    /**
     * The ability to select specific headers to include in the downloaded csv file
     */
    customizable: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * csv customizable dialog title
     */
    dialogTitle: {
      type: String,
      required: false,
      default: 'Customize CSV File'
    },

    disabled: Boolean
  },
  data () {
    return {
      dialog: false,
      selectedHeaders: [],
      preparing: false,
      data: []
    }
  },
  computed: {
    /**
     * Headers list
     */
    headers () {
      return Object.keys(this.fields).map(header => {
        return {
          value: header,
          label: header
        }
      })
    },
    /**
     * None
     */
    nothingSelected () {
      return this.selectedHeaders.length === 0
    },
    /**
     * All
     */
    allSelected () {
      return this.selectedHeaders.length === this.headers.length
    },
    /**
     * Some
     */
    indeterminate () {
      return (!this.nothingSelected) && (!this.allSelected)
    }
  },
  watch: {
    /**
     * Dialog status watcher to pre-select headers
     */
    dialog: {
      handler (show) {
        if (show) {
          this.selectedHeaders = Object.keys(this.fields)
        }
      },
      immediate: true
    }
  },
  methods: {
    /**
     * Select all click
     */
    selectAll () {
      this.selectedHeaders = (this.nothingSelected || this.indeterminate)
        ? [...this.headers.map(header => header.value)]
        : []
    },
    /**
     * Customized download button click
     */
    download (headers) {
      headers = headers || Object.keys(this.fields)
      this.$set(this, 'preparing', true)

      return Promise.resolve()
        .then(() => {
          return this.datasource()
        })
        .then((data) => {
          const rows = []
          for (let row of data) {
            rows.push(this.processRow(headers, row))
          }
          const csvFile = new CsvFile({ filename: this.name, headers, rows, removeUnusedHeaders: false })
          return csvFile.download()
        })
        .then(() => {
          this.$set(this, 'preparing', false)
          this.$set(this, 'dialog', false)
        })
        .catch(error => {
          this.$set(this, 'preparing', false)
          this.$set(this, 'dialog', false)
          Bus.$emit('alert', { color: 'error', message: error })
          console.log(error)
        })
    },

    processRow (headers, row) {
      const result = {}
      for (let key of headers) {
        let attribute = this.fields[key]
        let value
        let formatter
        if (typeof attribute === 'object') {
          attribute = attribute['attribute']
          formatter = attribute['formatter']
        }
        if (typeof attribute === 'string') {
          value = this.resolveAttribute(attribute, row)
        } else if (typeof attribute === 'function') {
          value = attribute(key, row)
        } else {
          throw Error('Unsupported attribute type for ' + key + ': ' + (typeof attribute))
        }
        if (formatter !== undefined) {
          value = this[formatter + 'Formatter'](value)
        }
        if (value !== undefined) {
          result[key] = value
        }
      }
      return result
    },

    resolveAttribute (path, obj) {
      const parts = path.split('.')
      for (let part of parts) {
        if (obj === null || obj === undefined) {
          return undefined
        }
        obj = obj[part]
      }
      return obj
    },

    dateFormatter (value) {
      return moment.utc(value).format('YYYY-MM-DD HH:mm')
    }
  }
}
</script>
